/**
 * Adds absolutely spaced elements representing each page with different background colors
 * Can be used to visualize the pages and spacing between them.
 * Requires debug to be true and manually commented out
 */
const addPageSpacers = (pageHeight: number, contentDocument: Document) => {
  const bodyHeight = contentDocument.body.getBoundingClientRect().height;

  const numberOfPages = Math.ceil(bodyHeight / pageHeight);

  // Adds absolutely spaced elements representing each page with different background colors
  // Can be used to visualize the pages and spacing between them
  for (let i = 0; i < numberOfPages; i++) {
    const spacingElement = document.createElement('div');
    spacingElement.style.position = 'absolute';
    spacingElement.style.top = `${i * pageHeight}px`;
    spacingElement.style.left = '0';
    spacingElement.style.width = '100%';
    spacingElement.style.height = `${pageHeight}px`;
    spacingElement.style.backgroundColor = `hsl(${(i * 360) / numberOfPages}, 100%, 50%)`;
    spacingElement.style.zIndex = '-1';
    // Add a data attribute to the spacing element to indicate the page number
    spacingElement.setAttribute('data-printty-page-number', i.toString());
    // Add a data attribute to the spacing element to indicate the page height
    spacingElement.setAttribute('data-printty-page-height', pageHeight.toString());

    // Needs to be uncommented to see the page spacers
    contentDocument.body.appendChild(spacingElement);
  }
};

/**
 * Creates dynamic spacing between elements to prevent fragmentation
 * @param elements - The elements to create spacing between
 * @param pageHeight - The height of each page
 * @param gap - The gap between each element
 * @param debug - Whether to add page spacers for debugging
 * @param contentDocument - The document to create the spacing in
 */
export function createDynamicSpacing(
  elements: NodeListOf<Element>,
  pageHeight: number,
  gap: number,
  debug: boolean,
  contentDocument: Document
) {
  elements.forEach((element, index) => {
    // Insert gap between all elements except the first one
    if (gap && index !== 0) {
      const spacingElement = document.createElement('div');
      spacingElement.style.height = `${gap}px`;
      element.parentNode?.insertBefore(spacingElement, element);
    }

    const elementRect = element.getBoundingClientRect();
    const spaceFromTop = elementRect.top;
    const elementHeight = elementRect.height;

    const currentPage = Math.floor(spaceFromTop / pageHeight);
    const spaceLeftOnPage = (currentPage + 1) * pageHeight - spaceFromTop;

    // If element doesn't fit on current page, move it to next page
    if (elementHeight > spaceLeftOnPage) {
      const spacingElement = document.createElement('div');
      const extraSpaceNeeded = pageHeight - (spaceFromTop % pageHeight);
      spacingElement.style.height = `${extraSpaceNeeded}px`;
      if (debug) {
        spacingElement.style.backgroundColor = 'red';
      }
      element.parentNode?.insertBefore(spacingElement, element);
    }
  });
  if (debug) {
    addPageSpacers(pageHeight, contentDocument);
  }
}
